import React, { useState } from 'react';
import {
    useRecordContext,
    useDataProvider,
    useNotify,
    useRefresh,
    useTranslate,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    BooleanField,
    Button,
    usePermissions,
} from 'react-admin';
import {
    Box,
    IconButton,
    Chip,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Sms as SmsIcon,
    WhatsApp as WhatsAppIcon,
    NotificationsActive as PushIcon,
    Chat as SlackIcon,
} from '@mui/icons-material';
import { AddMembersDialog } from './AddMembersDialog';
import { MemberChannelOverride } from './MemberChannelOverride';

const channelIcons: Record<string, React.ReactElement> = {
    email: <EmailIcon sx={{ fontSize: 14 }} />,
    sms: <SmsIcon sx={{ fontSize: 14 }} />,
    whatsapp: <WhatsAppIcon sx={{ fontSize: 14 }} />,
    push: <PushIcon sx={{ fontSize: 14 }} />,
    slack: <SlackIcon sx={{ fontSize: 14 }} />,
};

const getChannelColor = (channel: string) => {
    const colors: Record<string, string> = {
        email: '#1976D2',
        sms: '#4CAF50',
        whatsapp: '#25D366',
        push: '#FF6B35',
        slack: '#4A154B',
    };
    return colors[channel] || '#757575';
};

const wrappedChipSx = {
    maxWidth: '100%',
    minWidth: 0,
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.2,
        py: 0.5,
    },
};

const ChannelsField: React.FC<{ groupChannels?: string[] }> = ({ groupChannels = ['email'] }) => {
    const member = useRecordContext();
    const translate = useTranslate();

    const channels = member?.channelsOverride || groupChannels;
    const isOverridden = !!member?.channelsOverride;

    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            {channels.map((channel: string) => (
                <Chip
                    key={channel}
                    icon={channelIcons[channel]}
                    label={channel}
                    size="small"
                    variant={isOverridden ? "filled" : "outlined"}
                    sx={{
                        borderColor: getChannelColor(channel),
                        color: isOverridden ? 'white' : getChannelColor(channel),
                        backgroundColor: isOverridden ? getChannelColor(channel) : 'transparent',
                        '& .MuiChip-icon': {
                            color: isOverridden ? 'white' : getChannelColor(channel),
                        },
                    }}
                />
            ))}
            {isOverridden && (
                <Chip
                    label={translate('resources.notification-groups.members.overridden')}
                    size="small"
                    color="warning"
                    variant="outlined"
                />
            )}
        </Stack>
    );
};

interface MobileMembersListProps {
    members: any[];
    groupChannels: string[];
    canWrite: boolean;
    onEdit: (member: any) => void;
    onDelete: (member: any) => void;
}

const MobileMembersList: React.FC<MobileMembersListProps> = ({
    members,
    groupChannels,
    canWrite,
    onEdit,
    onDelete,
}) => {
    const translate = useTranslate();

    return (
        <Box>
            {members.map((member: any) => {
                const channels = member.channelsOverride || groupChannels;
                const isOverridden = !!member.channelsOverride;

                return (
                    <Box
                        key={member.id || member.userId}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: 1 }}
                            >
                                {member.userName}
                            </Typography>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: member.active ? '#4CAF50' : '#F44336',
                                    flexShrink: 0,
                                }}
                            />
                        </Box>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.5 }}
                        >
                            {member.userEmail}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                            {member.userRole && (
                                <Chip
                                    label={member.userRole}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ...wrappedChipSx, height: 'auto' }}
                                />
                            )}
                            {channels.map((channel: string) => (
                                <Chip
                                    key={channel}
                                    icon={channelIcons[channel]}
                                    label={channel}
                                    size="small"
                                    variant={isOverridden ? "filled" : "outlined"}
                                    sx={{
                                        ...wrappedChipSx,
                                        height: 'auto',
                                        borderColor: getChannelColor(channel),
                                        color: isOverridden ? 'white' : getChannelColor(channel),
                                        backgroundColor: isOverridden ? getChannelColor(channel) : 'transparent',
                                        '& .MuiChip-icon': {
                                            color: isOverridden ? 'white' : getChannelColor(channel),
                                        },
                                    }}
                                />
                            ))}
                            {isOverridden && (
                                <Chip
                                    label={translate('resources.notification-groups.members.overridden')}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ ...wrappedChipSx, height: 'auto' }}
                                />
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">
                                {member.addedAt ? new Date(member.addedAt).toLocaleDateString() : ''}
                            </Typography>
                            {canWrite && (
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip title={translate('resources.notification-groups.members.editChannels')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onEdit(member)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={translate('resources.notification-groups.members.remove')}>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => onDelete(member)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export const MembersTab = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [members, setMembers] = useState(record?.members || []);
    const [loading, setLoading] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editMember, setEditMember] = useState<any>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<any>(null);

    const canWrite = permissions?.includes('notifications:write:all');

    const loadMembers = async () => {
        if (!record?.id) return;

        setLoading(true);
        try {
            const { data } = await dataProvider.getOne('notification-groups', { id: record.id });
            setMembers(data.members || []);
        } catch (error) {
            notify('resources.notification-groups.members.loadError', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleAddMembers = async (userIds: string[], channelsOverride?: string[]) => {
        if (!record?.id) return;

        try {
            const result = await dataProvider.create(`notification-groups/${record.id}/members`, {
                data: { userIds, channelsOverride }
            });
            console.log('Add members result:', result);
            notify('resources.notification-groups.members.addSuccess', { type: 'success' });
            await loadMembers();
            setAddDialogOpen(false);
        } catch (error) {
            console.error('Error adding members:', error);
            notify('resources.notification-groups.members.addError', { type: 'error' });
        }
    };

    const handleUpdateMember = async (userId: string, channelsOverride: string[] | null, active: boolean) => {
        if (!record?.id) return;

        try {
            await dataProvider.update(`notification-groups/${record.id}/members`, {
                id: userId,
                data: { channelsOverride, active },
                previousData: { id: userId }
            });
            notify('resources.notification-groups.members.updateSuccess', { type: 'success' });
            await loadMembers();
            setEditMember(null);
        } catch (error) {
            notify('resources.notification-groups.members.updateError', { type: 'error' });
        }
    };

    const handleDeleteMember = async (userId: string) => {
        if (!record?.id) return;

        try {
            await dataProvider.delete(`notification-groups/${record.id}/members`, {
                id: userId,
                previousData: { id: userId }
            });
            notify('resources.notification-groups.members.removeSuccess', { type: 'success' });
            await loadMembers();
            setDeleteConfirm(null);
        } catch (error) {
            notify('resources.notification-groups.members.removeError', { type: 'error' });
        }
    };

    const ActionButtons = () => {
        const member = useRecordContext();

        if (!canWrite) return null;

        const handleEdit = (e: React.MouseEvent) => {
            e.stopPropagation();
            console.log('Edit member:', member);
            setEditMember(member);
        };

        const handleDelete = (e: React.MouseEvent) => {
            e.stopPropagation();
            console.log('Delete member:', member);
            setDeleteConfirm(member);
        };

        return (
            <Stack direction="row" spacing={1}>
                <Tooltip title={translate('resources.notification-groups.members.editChannels')}>
                    <IconButton
                        size="small"
                        onClick={handleEdit}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={translate('resources.notification-groups.members.remove')}>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={handleDelete}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
        );
    };

    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">
                    {translate('resources.notification-groups.members.title')} ({members.length})
                </Typography>
                {canWrite && (
                    <Button
                        label={translate('resources.notification-groups.members.add')}
                        onClick={() => setAddDialogOpen(true)}
                        startIcon={<AddIcon />}
                    />
                )}
            </Box>

            {members.length === 0 ? (
                <Box sx={{
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: '#F5F5F5',
                    borderRadius: 1
                }}>
                    <Typography color="text.secondary">
                        {translate('resources.notification-groups.members.empty')}
                    </Typography>
                </Box>
            ) : isMobile ? (
                <MobileMembersList
                    members={members}
                    groupChannels={record?.channels || ['email']}
                    canWrite={!!canWrite}
                    onEdit={(member) => setEditMember(member)}
                    onDelete={(member) => setDeleteConfirm(member)}
                />
            ) : (
                <Datagrid
                    data={members}
                    bulkActionButtons={false}
                    rowClick={false}
                    sx={{
                        backgroundColor: 'white',
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: '#F5F5F5',
                            fontWeight: 600,
                        },
                        '& .RaDatagrid-row': {
                            cursor: 'default',
                        },
                    }}
                >
                    <TextField
                        source="userName"
                        label="resources.notification-groups.members.fields.name"
                    />
                    <TextField
                        source="userEmail"
                        label="resources.notification-groups.members.fields.email"
                    />
                    <TextField
                        source="userRole"
                        label="resources.notification-groups.members.fields.role"
                    />
                    <FunctionField
                        label="resources.notification-groups.members.fields.channels"
                        render={() => <ChannelsField groupChannels={record?.channels} />}
                    />
                    <BooleanField
                        source="active"
                        label="resources.notification-groups.members.fields.active"
                    />
                    <DateField
                        source="addedAt"
                        label="resources.notification-groups.members.fields.addedAt"
                        showTime
                    />
                    <FunctionField
                        label="resources.notification-groups.members.fields.actions"
                        render={() => <ActionButtons />}
                    />
                </Datagrid>
            )}

            <AddMembersDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                onAdd={handleAddMembers}
                existingMemberIds={members.map((m: any) => m.userId)}
                groupChannels={record?.channels || ['email']}
            />

            {editMember && (
                <MemberChannelOverride
                    open={!!editMember}
                    member={editMember}
                    groupChannels={record?.channels || ['email']}
                    onClose={() => setEditMember(null)}
                    onSave={(channels, active) =>
                        handleUpdateMember(editMember.userId, channels, active)
                    }
                />
            )}

            <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>
                    {translate('resources.notification-groups.members.removeConfirm.title')}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {translate('resources.notification-groups.members.removeConfirm.content', {
                            name: deleteConfirm?.userName
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button
                        onClick={() => handleDeleteMember(deleteConfirm.userId)}
                        color="error"
                        variant="contained"
                    >
                        {translate('ra.action.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};